import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Icon } from '~/atomic/atoms/Icon/view';
import { Text } from '~/atomic/atoms/Text/view';
import { useFeature } from '~/store/entitlements/useFeature';

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  freemium: 'Freemium',
  premium_basic: 'Premium Basic',
  premium_pro: 'Premium Pro',
  premium_enterprise: 'Premium Enterprise',
};

type Variant = 'auto' | 'full' | 'compact';

interface LockedFeatureOverlayProps {
  feature: string;
  children: ReactNode;
  title?: string;
  description?: string;
  ctaText?: string;
  onClickCta?: () => void;
  blurPx?: number;
  variant?: Variant;
  className?: string;
}

function autoCopy(state: string, upgradeRequired: string | null) {
  if (state === 'expired') {
    return {
      planLabel: 'renovação',
      title: 'Seu acesso expirou',
      description:
        'Renove o plano para continuar usando esta funcionalidade na sua conta.',
    };
  }
  if (state === 'freemium') {
    return {
      planLabel: upgradeRequired
        ? PLAN_LABEL[upgradeRequired] ?? upgradeRequired
        : 'Premium',
      title: 'Você atingiu o limite do mês',
      description:
        'Faça upgrade para acesso ilimitado e nunca mais se preocupe com cotas.',
    };
  }
  if (upgradeRequired) {
    const label = PLAN_LABEL[upgradeRequired] ?? upgradeRequired;
    return {
      planLabel: label,
      title: `Disponível no ${label}`,
      description: 'Atualize seu plano para liberar este recurso.',
    };
  }
  return {
    planLabel: 'Premium',
    title: 'Recurso premium',
    description: 'Atualize seu plano para acessar esta funcionalidade.',
  };
}

export default function LockedFeatureOverlay({
  feature,
  children,
  title,
  description,
  ctaText = 'Conhecer planos',
  onClickCta,
  blurPx = 5,
  variant = 'full',
  className,
}: LockedFeatureOverlayProps) {
  const { entitlement, isAvailable, isQuotaExhausted } = useFeature(feature);
  const blocked = !isAvailable || isQuotaExhausted;
  if (!blocked) return <>{children}</>;

  const copy = autoCopy(entitlement.state, entitlement.upgradeRequired);
  const finalTitle = title ?? copy.title;
  const finalDescription = description ?? copy.description;
  const isCompact = variant === 'compact';

  return (
    <div className={clsx('relative overflow-hidden rounded-medium', className)}>
      <div
        aria-hidden
        className="pointer-events-none select-none"
        style={{ filter: `blur(${blurPx}px)`, opacity: 0.5 }}
      >
        {children}
      </div>

      <div
        className={clsx(
          'absolute inset-0 flex bg-intense-01/85 backdrop-blur-[1px]',
          isCompact ? 'items-center px-smaller py-quark' : 'items-center justify-center px-medium py-large',
        )}
      >
        {isCompact ? (
          <article className="flex w-full items-center gap-smaller rounded-medium border border-support-01 bg-intense-01 px-small py-quark shadow-level1">
            <span className="inline-flex shrink-0 items-center rounded-full bg-primary-01 px-nano py-quark text-[10px] font-bold uppercase tracking-[0.12em] text-intense-01">
              {copy.planLabel}
            </span>
            <Text
              className="flex-1 truncate text-bodySM color-complement-01"
              numberOfLines={1}
            >
              {finalTitle}
            </Text>
            <button
              type="button"
              onClick={onClickCta}
              className="inline-flex shrink-0 items-center gap-quark rounded-nano border border-primary-01 px-nano py-quark text-caption font-medium text-primary-01 transition-colors hover:bg-support-01 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/30"
            >
              {ctaText}
              <Icon
                name="icon-chevron-right"
                size={12}
                color="#1D4733"
                rounded={false}
              />
            </button>
          </article>
        ) : (
          <article className="relative w-full max-w-md overflow-hidden rounded-medium border border-support-01 bg-intense-01 shadow-level1">
            <div className="flex h-[6px]">
              <div className="w-1/2 bg-primary-01" />
              <div className="w-1/4 bg-secondary-01" />
              <div className="w-1/4 bg-support-01" />
            </div>

            <div className="p-medium">
              <div className="flex items-center gap-quark">
                <span className="inline-flex items-center rounded-full bg-primary-01 px-nano py-quark text-[10px] font-bold uppercase tracking-[0.12em] text-intense-01">
                  {copy.planLabel}
                </span>
                <span className="inline-flex items-center gap-quark text-caption text-intense-05">
                  <Icon
                    name="icon-graphic-up"
                    size={12}
                    color="#3F7D5F"
                    rounded={false}
                  />
                  <span>plano necessário</span>
                </span>
              </div>

              <Text className="mt-smaller text-headingH2 color-complement-01">
                {finalTitle}
              </Text>
              <Text className="mt-quark text-bodySM color-intense-06">
                {finalDescription}
              </Text>

              <button
                type="button"
                onClick={onClickCta}
                className="mt-medium inline-flex items-center gap-quark rounded-nano border border-primary-01 px-small py-quark text-bodyMD font-medium text-primary-01 transition-colors hover:bg-support-01 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/30"
              >
                {ctaText}
                <Icon
                  name="icon-chevron-right"
                  size={14}
                  color="#1D4733"
                  rounded={false}
                />
              </button>
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
